import ChevronLeft from '@mdi/svg/svg/chevron-left.svg?react';
import AuditLogIcon from '@mdi/svg/svg/clipboard-text-clock-outline.svg?react';
import Close from '@mdi/svg/svg/close-circle.svg?react';
import { useClientVD } from '@solinkcorp/agent/hooks/useClientVD';
import { honoFetcher } from '@solinkcorp/agent/lib/honoFetcher';
import { type EventFlow, type EventState } from '@solinkcorp/api-types';
import { type SupportedLanguages } from '@solinkcorp/locale';
import { Flex, Grid, IconWithTooltip, MDIIcon } from '@solinkcorp/solink-components';
import classNames from 'classnames';
import React, { useCallback } from 'react';
import useLocale from 'src/hooks/useLocale';
import { type EventController } from 'src/hooks/useSolinkEvents';
import LocaleUtil from 'utils/LocaleUtil';
import { WORKFLOW_GO_BACK_RESPONSE } from '../../../SettingsPage/EventFlowPage/constants';
import { EVENT_TYPE_WIDTH } from '../constant';
import { EventColoredCollapsibleSection } from '../EventColoredCollapsibleSection/EventColoredCollapsibleSection';
import EventExpandPopover from '../EventExpandPopover/EventExpandPopover';
import AuditLog from './AuditLog/AuditLog';
import useAuditLog from './AuditLog/useAuditLog';
import styles from './EventVerify.module.sass';
import useEventState from './useEventState';
import VerificationConclusion from './VerificationConclusion';

function capitalizeFirstLetter(string: string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

function getI18nPrompt(node: EventState['node'], localeCode: SupportedLanguages) {
  const prompt = node.i18n[localeCode][node.prompt];
  return prompt ? capitalizeFirstLetter(prompt) : capitalizeFirstLetter(node.prompt);
}

function getI18nValue(node: EventState['node'], value: string, localeCode: SupportedLanguages) {
  const i18nValue = node.i18n[localeCode][value];
  return i18nValue ? capitalizeFirstLetter(i18nValue) : capitalizeFirstLetter(value);
}

interface EventVerifyProps {
  eventColor: string;
  eventId: string;
  workflowIdOnEvent?: string;
  workflow?: EventFlow;
  updateEvent: EventController['updateEvent'];
  conclusion?: string;
  conclusionReason?: string;
  // If true, the audit log will be shown directly under the verify content.
  // If false (default), the audit log will be shown in a popover.
  displayAuditLogInline?: boolean;
  /**
   * Render on a light surface instead of the solid event-colour fill: a plain
   * (transparent) wrapper with dark prompt text and neutral-outlined answer
   * buttons. Used by the NDE POS Review panel, whose card supplies its own
   * background + accent. Defaults to the colour-filled Events-page look.
   */
  plain?: boolean;
}

const EventVerify: React.FC<EventVerifyProps> = (props) => {
  const {
    eventColor,
    eventId,
    workflow,
    workflowIdOnEvent,
    updateEvent,
    conclusion,
    conclusionReason,
    displayAuditLogInline = false,
    plain = false,
  } = props;

  const isVerified = !!conclusion;

  // If the event is verified, prioritize the eventFlowId logged in the event
  // otherwise, prioritize the workflow ID from the event flow.
  // For legacy events without a eventFlowId, use the ID of the currently applied workflow.
  // If the workflow isn't the original one, the state
  let workflowId = workflow?.id;
  let workflowName = workflow?.name;
  if (isVerified && workflowIdOnEvent && workflowIdOnEvent !== workflow?.id) {
    workflowId = workflowIdOnEvent;
    workflowName = undefined;
  }

  const isAgentWorkflow =
    workflow?.eventFlowMetadata?.precondition?.fieldPath === 'details.agentId';
  const agentId = workflow?.eventFlowMetadata?.precondition?.value;

  const client = useClientVD();
  const onEventStateUpdated = useCallback(
    async (request: Record<string, unknown>) => {
      if (!isAgentWorkflow || !eventId || !agentId) return;
      try {
        if (request.transition === 'yes' || request.transition === 'no') {
          const classification = request.transition === 'yes' ? 'present' : 'absent';
          honoFetcher(
            client.datasets.items.$post({
              json: {
                agentId,
                eventId,
                classification,
                startOffset: 10,
                endOffset: 10,
              },
            }),
          ).catch(() => {});
        }
      } catch (err) {
        console.error('Failed to add event to dataset:', err);
      }
    },
    [isAgentWorkflow, eventId, agentId, client.datasets.items],
  );

  const locale = useLocale();
  const {
    currentState: currentEventState,
    updateEventState,
    isEventStateLoading,
    resetEventFlowState,
    removeConclusions,
    getEventStateHistory,
  } = useEventState({
    eventId,
    workflowId,
    workflowName,
    updateEvent,
    isEventVerified: isVerified,
    conclusionReason,
    onEventStateUpdated,
  });

  const {
    auditLog,
    isLoading: isAuditLogLoading,
    isOpen: isAuditLogOpen,
    toggleAuditLog,
  } = useAuditLog(getEventStateHistory);

  const uniqueId = React.useId();
  const auditLogButtonId = `audit-log-button-${uniqueId}`;
  const auditLogButtonClass = classNames(styles.auditLogButton, {
    [styles.auditLogButtonActive]: isAuditLogOpen,
  });

  const node = currentEventState?.node;
  const isEmbeddedButtons = node?.controlType === 'embedded-buttons';

  const updatedBy =
    currentEventState?.data?.type === 'verification-prompt'
      ? currentEventState?.data?.updatedBy
      : currentEventState?.data?.resetBy;
  const updatedTime = currentEventState?.data?.updatedTime;

  const hasGoBack = node?.values?.includes(WORKFLOW_GO_BACK_RESPONSE);
  const answers = React.useMemo(() => {
    return node?.values
      ?.filter((value: string) => value !== WORKFLOW_GO_BACK_RESPONSE)
      ?.map((value: string) => ({
        value,
        label: getI18nValue(node, value, locale.locale),
      }));
  }, [node, locale.locale]);

  const shouldShowNotSupportedWarning = node?.controlType && !isEmbeddedButtons;
  const shouldShowResults = isVerified;
  const shouldShowPromptAndButtons = node && isEmbeddedButtons && !shouldShowResults;
  const shouldShowLoading = isEventStateLoading || (!node && !isVerified);
  const shouldShowWaitingUpdate =
    !isVerified && !isEventStateLoading && currentEventState?.state === 'CLOSED';
  const result = conclusionReason || conclusion;
  const allowRestartWorkflow = result && workflow;
  const verifiedWorkflowNotFound = isVerified && !isEventStateLoading && !currentEventState;
  const shouldShowVerifiedEventFlowState = isVerified && !isEventStateLoading && currentEventState;

  // If the audit log is shown in a popover, the event verify UI is in the event card.
  const isShownInEventCard = !displayAuditLogInline;
  const backButtonSpacing = isShownInEventCard ? EVENT_TYPE_WIDTH : EVENT_TYPE_WIDTH + 10;

  const wrapperExtraStyle = hasGoBack
    ? { paddingLeft: 0 }
    : !isShownInEventCard
      ? { paddingLeft: `${EVENT_TYPE_WIDTH}px`, paddingRight: `${EVENT_TYPE_WIDTH}px` }
      : undefined;
  const promptAndButtonsGridTemplateColumns = hasGoBack ? `${backButtonSpacing}px 1fr` : '1fr';

  const nodeTypeNotSupported = shouldShowNotSupportedWarning
    ? LocaleUtil.getLocalizedStr({
        string: locale.eventState.nodeTypeNotSupported,
        replacements: { type: node.controlType },
      })
    : '';

  const auditLogContent = (
    <AuditLog auditLog={auditLog} isLoading={isAuditLogLoading} toggleAuditLog={toggleAuditLog} />
  );

  const content = (
    <>
      {shouldShowPromptAndButtons && (
        <Grid
          alignItems="center"
          justifyItems="flex-start"
          gridTemplateColumns={promptAndButtonsGridTemplateColumns}
        >
          {hasGoBack && (
            <button
              type="button"
              title={locale.eventState.goBack}
              className={classNames(styles.backButton, { [styles.backButtonPlain]: plain })}
              onClick={() => updateEventState(WORKFLOW_GO_BACK_RESPONSE)}
            >
              <MDIIcon icon={ChevronLeft} size={EVENT_TYPE_WIDTH} />
            </button>
          )}
          <div className={styles.promptWrapper}>
            <div className={classNames(styles.prompt, { [styles.promptPlain]: plain })}>
              {getI18nPrompt(node, locale.locale)}
            </div>
            {answers && (
              <Flex className={styles.answers}>
                {answers.map(({ value, label }) => (
                  <button
                    type="button"
                    title={value}
                    className={classNames(styles.button, { [styles.buttonPlain]: plain })}
                    key={value}
                    onClick={() => updateEventState(value)}
                  >
                    {label}
                  </button>
                ))}
              </Flex>
            )}
          </div>
        </Grid>
      )}
      {shouldShowNotSupportedWarning && <div>{nodeTypeNotSupported}</div>}
      {shouldShowResults && (
        <VerificationConclusion
          plain={plain}
          result={result}
          showMetadata={!!shouldShowVerifiedEventFlowState}
          verifiedBy={updatedBy}
          updatedTime={updatedTime}
          verifiedWorkflowNotFound={verifiedWorkflowNotFound}
          resultAction={
            allowRestartWorkflow && (
              <IconWithTooltip
                className={styles.restartButton}
                icon={Close}
                iconSize={20}
                fill={eventColor}
                title={locale.general.remove}
                onClick={
                  workflowIdOnEvent === workflow?.id ? resetEventFlowState : removeConclusions
                }
              />
            )
          }
          trailing={
            // Audit log is only displayed if we know which workflow the result was verified with
            !!workflowIdOnEvent && (
              <>
                <div id={auditLogButtonId}>
                  <IconWithTooltip
                    icon={AuditLogIcon}
                    iconSize={19}
                    fill={isAuditLogOpen ? eventColor : 'var(--color-white)'}
                    title={locale.eventFlow.auditLog}
                    onClick={toggleAuditLog}
                    className={auditLogButtonClass}
                  />
                </div>
                {!displayAuditLogInline && (
                  <EventExpandPopover
                    isOpen={isAuditLogOpen}
                    togglePopover={toggleAuditLog}
                    popoverButtonId={auditLogButtonId}
                    horizontalOffset={-1}
                    verticalOffset={3}
                  >
                    {auditLogContent}
                  </EventExpandPopover>
                )}
              </>
            )
          }
        />
      )}
      {shouldShowLoading && <div className={styles.loading}>{locale.general.loading}...</div>}
      {shouldShowWaitingUpdate && <div>{locale.eventState.waitingUpdate}</div>}
      {!!workflowIdOnEvent && displayAuditLogInline && isAuditLogOpen && (
        <div className="mt-3 border-0 border-t-[1px] border-solid border-gray-200 pt-3">
          {auditLogContent}
        </div>
      )}
    </>
  );

  // Plain variant renders on the caller's own light surface (no event-colour
  // fill, no colour-strip left padding); the default keeps the Events-page look.
  return plain ? (
    <div className={styles.plainWrapper}>{content}</div>
  ) : (
    <EventColoredCollapsibleSection eventColor={eventColor} style={wrapperExtraStyle}>
      {content}
    </EventColoredCollapsibleSection>
  );
};

export default EventVerify;
